import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { login, logout } from "../features/auth.slice";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import { alertOpen, AlertValue } from "../features/alert.slice";
import {
  browserSessionPersistence,
  setPersistence,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { ButtonLoading } from "../components/spinner/Spiner";
import Button from "../components/button/Button";
import { baseColor } from "..";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import FormController from "../components/fields/FormController";
import Container from "../components/containers/Containers";
import jwtApi from "../api/Jwt.api";
import authApi from "../api/Auth.api";

const style = {
  color: {
    blue: `text-blue-600 hover:text-blue-500`,
    green: `text-indigo-600 hover:text-indigo-500`,
    red: `text-indigo-600 hover:text-indigo-500`,
    yellow: `text-indigo-600 hover:text-indigo-500`,
    indigo: `text-indigo-600 hover:text-indigo-500`,
  },
};

export const Login = () => {
  const dispatch = useDispatch();
  const navigation = useNavigate();
  const [isResetPass, setIsRestPass] = useState(false);
  const [isload, setIsLoad] = useState(false);
  // const isLogin = useSelector(loginSelector);
  interface LoginForm {
    requestEmail?: string;
    email: string;
    password: string;
    isResetPass?: boolean;
  }

  // バリデーションルール
  const schema = yup.object({
    email: yup.string()
    .when('isResetPass', {
      is: false,
      then: yup.string().email().required(),
      }
    ),
    password: yup.string()
      .when('isResetPass', {
        is: false,
        then: yup.string().required()
        }
      ),
    isResetPass: yup.boolean(),
    requestEmail: yup.string()
      .when('isResetPass', {
        is: true,
        then: yup.string().email().required(),
        }
      ),
  });

  const { control, handleSubmit, setValue } = useForm<LoginForm>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setValue('isResetPass', isResetPass);
  },[isResetPass, setValue]);
  //ログイン
  const onSubmit = async (data: LoginForm) => {
    setIsLoad(true);
    setPersistence(auth, browserSessionPersistence);
    signInWithEmailAndPassword(auth, data.email, data.password)
      .then(async (userCredential) => {
        // Signed in
        if (userCredential.user?.emailVerified) {
          console.log(userCredential);
          dispatch(login(userCredential));
          const currentUser = auth.currentUser;
          await jwtApi.requestGetJwt(currentUser?.email!, currentUser?.uid!);
          navigation("/");
        } else {
          logout();
          setIsLoad(false);
          dispatch(
            alertOpen(
              AlertValue(
                "登録したメールアドレスが認証されていません。メールを確認して認証してください。",
                "warning"
              )
            )
          );
        }
        // ...
      })
      .catch((error) => {
        const errorCode = error.code;
        let errorMessage: string = "ログインに失敗しました。";
        let errorMessageInfo: string;
        if (errorCode.toString() === "auth/wrong-password") {
          errorMessageInfo = "パスワードが間違っています。";
        } else if (errorCode.toString() === "auth/user-not-found") {
          errorMessageInfo = "未登録のメールアドレスです。";
        } else if (errorCode.toString() === "auth/invalid-email") {
          errorMessageInfo = "入力したメールアドレスの形式に問題があります。";
        } else if (errorCode.toString() === "auth/user-disabled") {
          errorMessageInfo = "入力したメールアドレスは無効です。";
        } else if (errorCode.toString() === "auth/too-many-requests") {
          errorMessageInfo =
            "複数回ログインに失敗したため、一時的に無効となりました。パスワードのリセットまたは、時間をおいてログインしてください";
        } else {
          errorMessageInfo = "";
        }
        console.error(errorCode);
        dispatch(
          alertOpen(AlertValue(errorMessage, "danger", errorMessageInfo))
        );
        setIsLoad(false);
      });
  };

  //パスワードリセット
  const submitPasswordResetEmail = async (login: LoginForm) => {
    auth.languageCode = 'ja';
    await authApi.requestSendPasswordResetEmail(login.requestEmail!)
      .then((resp) => {
        // メール送信成功
        dispatch(
          alertOpen(AlertValue("変更メールを送信しました。", "success"))
        );
      })
      .catch((error) => {
        // メール送信失敗
        dispatch(alertOpen(AlertValue("変更メールを送信に失敗しました", "danger")));
        console.error(error);
      });
    return;
  };

  const loginPage = () => {
    return (
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form
            className="space-y-3"
            onSubmit={handleSubmit(onSubmit)}
            method="POST"
            noValidate
          >
            <FormController
              control={control}
              name={"email"}
              label={"メールアドレス"}
              type={"email"}
            />
            <FormController
              control={control}
              name={"password"}
              label={"パスワード"}
              type={"password"}
            />
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <div
                  className={`font-medium ${style.color[baseColor]}`}
                  onClick={() => setIsRestPass(true)}
                >
                  パスワードをお忘れですか？
                </div>
              </div>
            </div>
            <div>
              <Button submit disabled={isload} block>
                {isload ? <ButtonLoading /> : "ログイン"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const resetPassPage = () => {
    return (
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form
            className="space-y-6"
            onSubmit={handleSubmit(submitPasswordResetEmail)}
            method="POST"
          >
            <div className="mb-4">
              <label
                htmlFor="password"
                className="mb-4 text-left block text-xs font-medium text-gray-500"
              >
                メールアドレスを入力してください
              </label>
              <FormController
                id={"requestEmail"}
                control={control}
                name={"requestEmail"}
                label={"メールアドレス"}
                type={"email"}
              />
            </div>

            <div>
              <button
                type="submit"
                className="pt-2 flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                変更メールの送信
              </button>
              <div className="text-right text-sm pt-2">
                <div
                  className={`cursor-pointer ${style.color[baseColor]}`}
                  onClick={() => setIsRestPass(false)}
                >
                  ログイン画面へ
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  };
  return (
    <Container>
      <h2 className="text-center text-3xl font-bold tracking-tight text-gray-900">
        イベントサポート 運営管理
      </h2>
      {isResetPass ? resetPassPage() : loginPage()}
    </Container>
  );
};
